import axios from 'axios';
import { getTokenData, refreshAccessToken, removeTokenData, saveTokenData } from './auth/AuthService';
import { REACT_APP_BACKEND_API_URL } from '../utils/config';

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

export const client = (isJSONType: boolean, isAPI: boolean, sentCookie: boolean = false) => {
  const baseURL = `${REACT_APP_BACKEND_API_URL}${isAPI ? '/api' : ''}`;
  const contentType = isJSONType ? 'application/json' : 'application/x-www-form-urlencoded';

  let headers = {
    'Content-Type': contentType,
  };

  if (isAPI) {
    const tokenData = getTokenData();
    headers = {
      ...headers,
      ...(tokenData ? { Authorization: `Bearer ${tokenData.accessToken}` } : {}),
    };
  }

  const instance = axios.create({
    baseURL,
    headers,
    timeout: 120_000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    withCredentials: sentCookie
  });
 
  if (isAPI) {
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  resolve(instance(originalRequest));
                },
                reject,
              });
            });
          }

          isRefreshing = true;

          try {
            const newTokenData = await refreshAccessToken();

            if (!newTokenData) {
              throw new Error('Token refresh failed');
            }

            saveTokenData(newTokenData);
            processQueue(null, newTokenData.accessToken);

            originalRequest.headers['Authorization'] = `Bearer ${newTokenData.accessToken}`;
            return instance(originalRequest);
          } catch (err) {
            processQueue(err, null);
            removeTokenData();
            window.location.href = '/login';
            return Promise.reject(err);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  return instance;
};
